import { Popover } from '@mantine/core';
import { useSetState, useUncontrolled } from '@mantine/hooks';
import { noop } from '@utils/noop';
import { forwardRef, memo, useState } from 'react';
import SearchFigma from '../SearchFigma/SearchFigma';
import { AutocompleteProps, IHasMore, AutocompleteType } from './types';
import { useMenuStyles, useAutocompleteStyles } from './Autocomplete.styles';

function Autocomplete(
  props: AutocompleteProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const {
    autocompleteItems,
    classNames,
    value,
    onClick = noop,
    onKeyDown = noop,
    onChange = noop,
    startSearch = noop,
    ...rest
  } = props;

  const [open, setOpen] = useState(false);
  const [_value, setValue] = useState(value ?? '');
  const [_searchValue, handleChange] = useUncontrolled({
    defaultValue: '',
    value,
    onChange,
  });

  const [optionState, setOptionState] = useSetState<IHasMore>({
    activeOption: -1,
    min: 0,
    max: -1,
    isEmpty: true,
  });

  const { activeOption, min, max, isEmpty } = optionState;
  function inputStartSearch(text: string) {
    // console.log('Search');
    // Close when we start searching.
    setOpen(false);
    // Sync values
    handleChange(text);
    setValue(text);
    // search and reset values
    startSearch(text);
    setOptionState({
      activeOption: -1,
    });
  }

  function getItems() {
    if (autocompleteItems) {
      return autocompleteItems({
        broadcast: setOptionState,
        activeOption,
        onChange: setValue,
        searchText: _searchValue,
        startSearch: inputStartSearch,
      });
    }
    return null;
  }

  const { classes } = useMenuStyles();
  const { input, ...remainingClassnames } = classNames || {};
  const { classes: autocompleteClasses } = useAutocompleteStyles(
    { open: open && !isEmpty },
    {
      name: 'Autocomplete',
      classNames: {
        input,
      },
    },
  );

  function onPopoverChange(state: boolean) {
    // console.log('Popover Change');
    setOpen(state);
    if (!state) {
      // reset position when we close
      setOptionState({
        activeOption: -1,
      });
    }
  }
  function onClickSearch(e: React.MouseEvent<HTMLInputElement>) {
    // console.log('Click');
    setOpen(true);
    onClick(e);
  }
  function onKeyboardDown(event: React.KeyboardEvent<HTMLInputElement>) {
    // if Up arrow
    const isUpKey = event.keyCode === 38;
    const shoudldGoUp = isUpKey && activeOption > min;
    if (shoudldGoUp) {
      setOptionState({
        activeOption: activeOption - 1,
      });
    }
    // if down arrow.
    const isDownKey = event.keyCode === 40;
    const shouldGoDown = isDownKey && activeOption < max;
    if (shouldGoDown) {
      setOptionState({
        activeOption: activeOption + 1,
      });
    }
    // Handle normal event
    onKeyDown(event);
    if (isUpKey || isDownKey) {
      event.preventDefault();
    }
  }
  function onSearchChange(text: string) {
    // console.log('Text Onchange');
    setOptionState({
      activeOption: -1,
    });
    handleChange(text);
    setValue(text);
    if (text.length > 0) setOpen(true); // Reopen when we continue typing.
  }

  function getValue() {
    if (activeOption > -1) return _value;
    return _searchValue;
  }
  return (
    <Popover
      offset={0}
      opened={open}
      onChange={onPopoverChange}
      classNames={classes}
      withinPortal
      width="target"
    >
      <Popover.Target>
        <SearchFigma
          value={getValue()}
          onClick={onClickSearch}
          onKeyDown={onKeyboardDown}
          onChange={onSearchChange}
          startSearch={inputStartSearch}
          ref={ref}
          classNames={{
            ...remainingClassnames,
            ...autocompleteClasses,
          }}
          {...rest}
        />
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <div>{getItems()}</div>
      </Popover.Dropdown>
    </Popover>
  );
}

export default memo(
  forwardRef<HTMLInputElement, AutocompleteProps>(Autocomplete),
) as AutocompleteType;
